import { useQuery } from '@tanstack/react-query'
import {
  isUsersApiEnabled,
  USER_TOKEN_KEY,
  USERS_API_URL,
} from 'utils/constants'
import { useLocalStorage } from './useLocalStorageState'
import { milliseconds } from 'date-fns'
import { useEffect } from 'react'
import { UserResponse } from 'utils/login'
import { useWallet } from '@solana/wallet-adapter-react'
import { toContextTuplesString } from 'utils/logging'

async function verifyAuthRequest(token: string): Promise<
  | { valid: false }
  | {
      valid: true
      user: UserResponse | null
    }
> {
  if (!isUsersApiEnabled()) return { valid: false }
  const resp = await fetch(`${USERS_API_URL}/user`, {
    headers: {
      authorization: token,
    },
  })
  if (!resp.ok) {
    if (resp.status === 401) {
      return { valid: false }
    }
    if (resp.status === 404) {
      return { valid: true, user: null }
    }

    throw new Error(
      `Error validating token: ${toContextTuplesString({
        status: resp.status,
        response: await resp.text(),
      })}`,
    )
  }
  return {
    valid: true,
    user: (await resp.json()) as UserResponse,
  }
}

export function useIsAuthorized() {
  const [token, setToken] = useLocalStorage(USER_TOKEN_KEY)
  const { publicKey } = useWallet()
  const walletPubkeyStr = publicKey?.toBase58()

  useEffect(() => {
    if (!token || !walletPubkeyStr) {
      return
    }

    const tokenPayload = getTokenPayload(token)
    if (
      tokenPayload != null &&
      typeof tokenPayload === 'object' &&
      'wallet' in tokenPayload &&
      typeof tokenPayload.wallet === 'string' &&
      tokenPayload.wallet !== walletPubkeyStr
    ) {
      setToken(null)
    }
  }, [setToken, token, walletPubkeyStr])

  const {
    data: authVerifyResult,
    isInitialLoading,
    refetch,
  } = useQuery({
    queryKey: ['verifyAuth', walletPubkeyStr, token],
    queryFn: async () => {
      if (!token) {
        throw new Error('No token provided')
      }

      return verifyAuthRequest(token)
    },
    enabled: !!(walletPubkeyStr && token),
    staleTime: milliseconds({ hours: 10 }),
    retry: 3,
    keepPreviousData: true,
  })

  useEffect(() => {
    if (!authVerifyResult) return

    if (!authVerifyResult.valid) {
      setToken(null)
    }
  }, [authVerifyResult, setToken])

  return {
    authenticated: token != null && !isInitialLoading,
    user: authVerifyResult?.valid ? authVerifyResult.user : null,
    loadingUser: isInitialLoading,
    refetchUser: refetch,
  }
}

function getTokenPayload(token: string) {
  const encodedPayload = token.split('.')[1]
  if (!encodedPayload) {
    return null
  }
  try {
    const payloadJsonString = new TextDecoder('utf-8', { fatal: true }).decode(
      Uint8Array.from(atob(encodedPayload), (m) => m.charCodeAt(0)),
    )
    return JSON.parse(payloadJsonString)
  } catch (e) {
    console.debug('error parsing JWT token payload', e)
    return null
  }
}
